<template>
  <SuiteTopicsTechnology :suite-data="contextData" />
</template>

<script setup lang="ts">
  import type { ITechnology } from '@/components/Shared/Suite/TopicsTechnology/index.vue';

  const { t } = useI18n({ useScope: 'local' });
  const { blobDomain } = useWebsite();
  const { provideResponsiveImgName } = useImagePath();

  const contextData = computed(
    (): ITechnology => ({
      component: 'topics-technology',
      meta: {
        navigable: '',
      },
      data: {
        sectionName: t('section-name'),
        title: t('title'),
        numberOfLine: 4,
        items: [
          {
            name: t('item-1-title'),
            description: t('item-1-description'),
            thumbnail: `${blobDomain.value}/wise-paas-marketplace/topics/doe/${provideResponsiveImgName('icon_technology_hardware-software-integration')}`,
          },
          {
            name: t('item-2-title'),
            description: t('item-2-description'),
            thumbnail: `${blobDomain.value}/wise-paas-marketplace/topics/doe/${provideResponsiveImgName('icon_technology_ready-to-use-applications')}`,
          },
          {
            name: t('item-3-title'),
            description: t('item-3-description'),
            thumbnail: `${blobDomain.value}/wise-paas-marketplace/topics/doe/${provideResponsiveImgName('icon_technology_scalable-multi-site-operations')}`,
          },
          {
            name: t('item-4-title'),
            description: t('item-4-description'),
            thumbnail: `${blobDomain.value}/wise-paas-marketplace/topics/doe/${provideResponsiveImgName('icon_technology_ai-ready-data-architecture')}`,
          },
        ],
      },
    })
  );
</script>

<i18n lang="json">
{
  "en-int": {
    "section-name": "Why Advantech WISE",
    "title": "What Modern Digital O&M Requires",
    "item-1-title": "Hardware-Software Integration",
    "item-1-description": "Align devices and software to enable <span class=\"font-bold text-adv_blue-400\">secure connectivity</span> and consistent data flows across site.",
    "item-2-title": "Ready-to-Use Applications",
    "item-2-description": "Deploy ready-made applications for device integration and remote O&M, enabling <span class=\"font-bold text-adv_blue-400\">faster rollout</span> with minimal setup effort.",
    "item-3-title": "Scalable Multi-Site Operations",
    "item-3-description": "Support <span class=\"font-bold text-adv_blue-400\">standardized workflows</span> and dashboards to improve visibility as environments scale.",
    "item-4-title": "AI-Ready Data Architecture",
    "item-4-description": "Structure operational and energy data to support <span class=\"font-bold text-adv_blue-400\">predictive maintenance</span> and data-driven optimization."
  },
  "en-jp": {
    "section-name": "Why Advantech WISE",
    "title": "What Modern Digital O&M Requires",
    "item-1-title": "Hardware-Software Integration",
    "item-1-description": "Align devices and software to enable <span class=\"font-bold text-adv_blue-400\">secure connectivity</span> and consistent data flows across site.",
    "item-2-title": "Ready-to-Use Applications",
    "item-2-description": "Deploy ready-made applications for device integration and remote O&M, enabling <span class=\"font-bold text-adv_blue-400\">faster rollout</span> with minimal setup effort.",
    "item-3-title": "Scalable Multi-Site Operations",
    "item-3-description": "Support <span class=\"font-bold text-adv_blue-400\">standardized workflows</span> and dashboards to improve visibility as environments scale.",
    "item-4-title": "AI-Ready Data Architecture",
    "item-4-description": "Structure operational and energy data to support <span class=\"font-bold text-adv_blue-400\">predictive maintenance</span> and data-driven optimization."
  },
  "en-tw": {
    "section-name": "Why Advantech WISE",
    "title": "What Modern Digital O&M Requires",
    "item-1-title": "Hardware-Software Integration",
    "item-1-description": "Align devices and software to enable <span class=\"font-bold text-adv_blue-400\">secure connectivity</span> and consistent data flows across site.",
    "item-2-title": "Ready-to-Use Applications",
    "item-2-description": "Deploy ready-made applications for device integration and remote O&M, enabling <span class=\"font-bold text-adv_blue-400\">faster rollout</span> with minimal setup effort.",
    "item-3-title": "Scalable Multi-Site Operations",
    "item-3-description": "Support <span class=\"font-bold text-adv_blue-400\">standardized workflows</span> and dashboards to improve visibility as environments scale.",
    "item-4-title": "AI-Ready Data Architecture",
    "item-4-description": "Structure operational and energy data to support <span class=\"font-bold text-adv_blue-400\">predictive maintenance</span> and data-driven optimization."
  },
  "en-kr": {
    "section-name": "Why Advantech WISE",
    "title": "What Modern Digital O&M Requires",
    "item-1-title": "Hardware-Software Integration",
    "item-1-description": "Align devices and software to enable <span class=\"font-bold text-adv_blue-400\">secure connectivity</span> and consistent data flows across site.",
    "item-2-title": "Ready-to-Use Applications",
    "item-2-description": "Deploy ready-made applications for device integration and remote O&M, enabling <span class=\"font-bold text-adv_blue-400\">faster rollout</span> with minimal setup effort.",
    "item-3-title": "Scalable Multi-Site Operations",
    "item-3-description": "Support <span class=\"font-bold text-adv_blue-400\">standardized workflows</span> and dashboards to improve visibility as environments scale.",
    "item-4-title": "AI-Ready Data Architecture",
    "item-4-description": "Structure operational and energy data to support <span class=\"font-bold text-adv_blue-400\">predictive maintenance</span> and data-driven optimization."
  },
  "en-americas": {
    "section-name": "Why Advantech WISE",
    "title": "What Modern Digital O&M Requires",
    "item-1-title": "Hardware-Software Integration",
    "item-1-description": "Align devices and software to enable <span class=\"font-bold text-adv_blue-400\">secure connectivity</span> and consistent data flows across site.",
    "item-2-title": "Ready-to-Use Applications",
    "item-2-description": "Deploy ready-made applications for device integration and remote O&M, enabling <span class=\"font-bold text-adv_blue-400\">faster rollout</span> with minimal setup effort.",
    "item-3-title": "Scalable Multi-Site Operations",
    "item-3-description": "Support <span class=\"font-bold text-adv_blue-400\">standardized workflows</span> and dashboards to improve visibility as environments scale.",
    "item-4-title": "AI-Ready Data Architecture",
    "item-4-description": "Structure operational and energy data to support <span class=\"font-bold text-adv_blue-400\">predictive maintenance</span> and data-driven optimization."
  },
  "en-asia-pacific": {
    "section-name": "Why Advantech WISE",
    "title": "What Modern Digital O&M Requires",
    "item-1-title": "Hardware-Software Integration",
    "item-1-description": "Align devices and software to enable <span class=\"font-bold text-adv_blue-400\">secure connectivity</span> and consistent data flows across site.",
    "item-2-title": "Ready-to-Use Applications",
    "item-2-description": "Deploy ready-made applications for device integration and remote O&M, enabling <span class=\"font-bold text-adv_blue-400\">faster rollout</span> with minimal setup effort.",
    "item-3-title": "Scalable Multi-Site Operations",
    "item-3-description": "Support <span class=\"font-bold text-adv_blue-400\">standardized workflows</span> and dashboards to improve visibility as environments scale.",
    "item-4-title": "AI-Ready Data Architecture",
    "item-4-description": "Structure operational and energy data to support <span class=\"font-bold text-adv_blue-400\">predictive maintenance</span> and data-driven optimization."
  },
  "en-emea": {
    "section-name": "Why Advantech WISE",
    "title": "What Modern Digital O&M Requires",
    "item-1-title": "Hardware-Software Integration",
    "item-1-description": "Align devices and software to enable <span class=\"font-bold text-adv_blue-400\">secure connectivity</span> and consistent data flows across site.",
    "item-2-title": "Ready-to-Use Applications",
    "item-2-description": "Deploy ready-made applications for device integration and remote O&M, enabling <span class=\"font-bold text-adv_blue-400\">faster rollout</span> with minimal setup effort.",
    "item-3-title": "Scalable Multi-Site Operations",
    "item-3-description": "Support <span class=\"font-bold text-adv_blue-400\">standardized workflows</span> and dashboards to improve visibility as environments scale.",
    "item-4-title": "AI-Ready Data Architecture",
    "item-4-description": "Structure operational and energy data to support <span class=\"font-bold text-adv_blue-400\">predictive maintenance</span> and data-driven optimization."
  },
  "ja-jp": {
    "section-name": "Advantech WISEを選ぶ理由",
    "title": "現代のデジタルO&Mに求められる主要能力",
    "description": "",
    "item-1-title": "ハードウェアとソフトウェアの統合",
    "item-1-description": "デバイスとソフトウェアを連携し、<span class=\"font-bold text-adv_blue-400\">セキュア</span>な接続と一貫したデータフローを実現します。",
    "item-2-title": "すぐに使えるアプリケーション",
    "item-2-description": "デバイス統合と遠隔O&M向けの既製アプリケーションを活用し、最小限のセットアップで<span class=\"font-bold text-adv_blue-400\">迅速な導入</span>を実現します。",
    "item-3-title": "スケーラブルなマルチサイト運用",
    "item-3-description": "<span class=\"font-bold text-adv_blue-400\">標準化されたワークフロー</span>とダッシュボードにより、拠点拡大時も一貫した可視性を維持します。",
    "item-4-title": "AI 活用を前提としたデータ基盤",
    "item-4-description": "運用・エネルギーデータを構造化し、<span class=\"font-bold text-adv_blue-400\">予知保全</span>やデータドリブン最適化を支援します。"
  },
  "zh-cn": {
    "section-name": "Why Advantech WISE",
    "title": "What Modern Digital O&M Requires",
    "item-1-title": "Hardware-Software Integration",
    "item-1-description": "Align devices and software to enable <span class=\"font-bold text-adv_blue-400\">secure connectivity</span> and consistent data flows across site.",
    "item-2-title": "Ready-to-Use Applications",
    "item-2-description": "Deploy ready-made applications for device integration and remote O&M, enabling <span class=\"font-bold text-adv_blue-400\">faster rollout</span> with minimal setup effort.",
    "item-3-title": "Scalable Multi-Site Operations",
    "item-3-description": "Support <span class=\"font-bold text-adv_blue-400\">standardized workflows</span> and dashboards to improve visibility as environments scale.",
    "item-4-title": "AI-Ready Data Architecture",
    "item-4-description": "Structure operational and energy data to support <span class=\"font-bold text-adv_blue-400\">predictive maintenance</span> and data-driven optimization."
  },
  "zh-tw": {
    "section-name": "為何選擇 Advantech WISE",
    "title": "現代數位維運的關鍵能力",
    "item-1-title": "軟硬體整合能力",
    "item-1-description": "整合設備與軟體，確保<span class=\"font-bold text-adv_blue-400\">安全連線</span>與穩定資料流",
    "item-2-title": "即用型工業App",
    "item-2-description": "提供預先整合的應用，最小部署成本<span class=\"font-bold text-adv_blue-400\">快速上線</span>",
    "item-3-title": "可擴展的多據點營運",
    "item-3-description": "透過<span class=\"font-bold text-adv_blue-400\">標準化流程</span>與儀表板，支援規模化集中管理",
    "item-4-title": "AI-ready 數據架構",
    "item-4-description": "以結構化數據，支援<span class=\"font-bold text-adv_blue-400\">預測性維護</span>與數據驅動優化"
  }
}
</i18n>
